import React, { useRef, useState, useCallback, useEffect } from 'react';
import maplibregl, { Map } from 'maplibre-gl';
import TeAraroaMap from '../Map/TeAraroaMap';
import api from '../../services/api';

interface TidePointMapProps {
  onLocationChange: (lat: number, lng: number) => void;
  initialLat?: number;
  initialLng?: number;
  height?: string;
}

interface TideStationPreview {
  requestedLatitude: number;
  requestedLongitude: number;
  actualLatitude: number;
  actualLongitude: number;
  distanceKm: number;
  stationName: string;
}

/**
 * Tide point selection map component.
 * Wraps TeAraroaMap and adds tide point marker placement functionality with NIWA station preview.
 */
const TidePointMap: React.FC<TidePointMapProps> = ({
  onLocationChange,
  initialLat,
  initialLng,
  height = '400px'
}) => {
  const mapRef = useRef<Map | null>(null);
  const requestMarker = useRef<maplibregl.Marker | null>(null);
  const stationMarker = useRef<maplibregl.Marker | null>(null);
  const connectionLine = useRef<string | null>(null);
  const onLocationChangeRef = useRef(onLocationChange);

  const [coordinates, setCoordinates] = useState<{lat: number, lng: number} | null>(
    initialLat && initialLng ? { lat: initialLat, lng: initialLng } : null
  );
  const [preview, setPreview] = useState<TideStationPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keep the ref updated with the latest callback
  useEffect(() => {
    onLocationChangeRef.current = onLocationChange;
  }, [onLocationChange]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      // Clean up markers and map resources
      if (requestMarker.current) {
        requestMarker.current.remove();
        requestMarker.current = null;
      }
      if (stationMarker.current) {
        stationMarker.current.remove();
        stationMarker.current = null;
      }
      if (connectionLine.current && mapRef.current) {
        const map = mapRef.current;
        try {
          if (map.getLayer(connectionLine.current)) {
            map.removeLayer(connectionLine.current);
          }
          if (map.getSource(connectionLine.current)) {
            map.removeSource(connectionLine.current);
          }
        } catch (error) {
          // Ignore errors during cleanup - map may already be destroyed
          console.warn('Error during map cleanup:', error);
        }
        connectionLine.current = null;
      }
    };
  }, []);

  // Fetch preview from API
  const fetchPreview = useCallback(async (lat: number, lng: number) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.post<TideStationPreview>('/api/TidePoints/preview', {
        latitude: lat,
        longitude: lng
      });

      setPreview(response.data);

      // Add station marker
      if (mapRef.current && stationMarker.current) {
        stationMarker.current.setLngLat([response.data.actualLongitude, response.data.actualLatitude]);
      } else if (mapRef.current) {
        const el = document.createElement('div');
        el.className = 'tide-station-marker';
        el.style.width = '20px';
        el.style.height = '20px';
        el.innerHTML = `
          <svg viewBox="0 0 24 24" fill="#10b981" stroke="white" stroke-width="2">
            <circle cx="12" cy="12" r="8"/>
            <path d="M12 8v8M8 12h8" stroke="white" stroke-width="2"/>
          </svg>
        `;

        stationMarker.current = new maplibregl.Marker({ element: el })
          .setLngLat([response.data.actualLongitude, response.data.actualLatitude])
          .setPopup(
            new maplibregl.Popup({ offset: 15 }).setHTML(`
              <div style="padding: 4px;">
                <h4 style="font-weight: bold; margin-bottom: 2px; font-size: 12px;">NIWA Tide Station</h4>
                <p style="font-size: 11px; color: #666;">
                  ${response.data.distanceKm.toFixed(1)} km from selected location
                </p>
              </div>
            `)
          )
          .addTo(mapRef.current);
      }

      // Draw connection line
      if (mapRef.current && response.data.distanceKm > 0.1) {
        const map = mapRef.current;

        // Remove existing line
        if (connectionLine.current) {
          if (map.getLayer(connectionLine.current)) {
            map.removeLayer(connectionLine.current);
          }
          if (map.getSource(connectionLine.current)) {
            map.removeSource(connectionLine.current);
          }
        }

        const lineId = 'preview-connection-line';
        connectionLine.current = lineId;

        map.addSource(lineId, {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: [
                [lng, lat],
                [response.data.actualLongitude, response.data.actualLatitude]
              ]
            }
          }
        });

        map.addLayer({
          id: lineId,
          type: 'line',
          source: lineId,
          paint: {
            'line-color': '#10b981',
            'line-width': 2,
            'line-dasharray': [4, 4]
          }
        });
      }
    } catch (err: any) {
      console.error('Error fetching tide station preview:', err);
      setError(err.response?.data?.message || 'Failed to fetch tide station preview');
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle coordinate updates
  const updateCoordinates = useCallback((lat: number, lng: number) => {
    setCoordinates({ lat, lng });
    onLocationChangeRef.current(lat, lng);
    fetchPreview(lat, lng);
  }, [fetchPreview]);

  // Handle map load - add initial marker if coordinates provided
  const handleMapLoad = useCallback((map: Map) => {
    mapRef.current = map;

    // Add initial marker if coordinates provided
    if (initialLat && initialLng) {
      requestMarker.current = new maplibregl.Marker({ color: '#3b82f6', draggable: true })
        .setLngLat([initialLng, initialLat])
        .addTo(map);

      requestMarker.current.on('dragend', () => {
        if (requestMarker.current) {
          const lngLat = requestMarker.current.getLngLat();
          updateCoordinates(lngLat.lat, lngLat.lng);
        }
      });

      // Fetch preview for initial location
      fetchPreview(initialLat, initialLng);
    }
  }, [initialLat, initialLng, fetchPreview, updateCoordinates]);

  // Handle map clicks to place/move marker
  const handleMapClick = useCallback((e: maplibregl.MapMouseEvent) => {
    const { lng, lat } = e.lngLat;

    if (requestMarker.current) {
      // Move existing marker
      requestMarker.current.setLngLat([lng, lat]);
    } else {
      // Create new marker
      if (mapRef.current) {
        requestMarker.current = new maplibregl.Marker({ color: '#3b82f6', draggable: true })
          .setLngLat([lng, lat])
          .addTo(mapRef.current);

        requestMarker.current.on('dragend', () => {
          if (requestMarker.current) {
            const lngLat = requestMarker.current.getLngLat();
            updateCoordinates(lngLat.lat, lngLat.lng);
          }
        });
      }
    }

    updateCoordinates(lat, lng);
  }, [updateCoordinates]);

  const clearMarker = () => {
    if (requestMarker.current) {
      requestMarker.current.remove();
      requestMarker.current = null;
    }
    if (stationMarker.current) {
      stationMarker.current.remove();
      stationMarker.current = null;
    }
    if (connectionLine.current && mapRef.current) {
      const map = mapRef.current;
      if (map.getLayer(connectionLine.current)) {
        map.removeLayer(connectionLine.current);
      }
      if (map.getSource(connectionLine.current)) {
        map.removeSource(connectionLine.current);
      }
      connectionLine.current = null;
    }
    setCoordinates(null);
    setPreview(null);
    setError(null);
  };

  // Determine initial center and zoom
  const center: [number, number] = (initialLng && initialLat)
    ? [initialLng, initialLat]
    : [172.6, -43.5]; // Default: New Zealand
  const zoom = initialLat && initialLng ? 10 : 5;

  return (
    <div className="tide-point-map-container">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium text-gray-900">Tide Point Location</h4>
        </div>
        {coordinates && (
          <button
            type="button"
            onClick={clearMarker}
            className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded border"
          >
            Clear Location
          </button>
        )}
      </div>

      {/* Map Instructions Banner */}
      <div className="mb-3 px-4 py-2.5 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-center gap-3 text-sm text-blue-700">
          <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="flex-1 flex flex-wrap items-center gap-x-6 gap-y-1">
            <span>Click the map to place your tide point marker</span>
            <span className="text-blue-400">•</span>
            <span>Drag the blue marker to adjust position</span>
            <span className="text-blue-400">•</span>
            <span>Green marker shows the NIWA tide station</span>
          </div>
        </div>
      </div>

      <TeAraroaMap
        center={center}
        zoom={zoom}
        height={height}
        onMapLoad={handleMapLoad}
        onMapClick={handleMapClick}
      />

      {error && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Combined Info Card with Loading State */}
      {coordinates && (
        <div className="mt-3 p-4 bg-gradient-to-br from-blue-50 to-green-50 border border-blue-200 rounded-lg">
          {loading ? (
            <div className="flex items-center gap-3">
              <svg className="animate-spin h-5 w-5 text-blue-600 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-sm text-blue-800 font-medium">Checking NIWA tide station location...</span>
            </div>
          ) : preview ? (
            <div className="grid grid-cols-2 gap-4">
              {/* Selected Location */}
              <div className="bg-white/50 p-3 rounded border border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span className="text-xs font-semibold text-blue-900 uppercase tracking-wide">Your Location</span>
                </div>
                <div className="text-xs text-blue-700 space-y-0.5">
                  <div>Lat: {coordinates.lat.toFixed(6)}</div>
                  <div>Lng: {coordinates.lng.toFixed(6)}</div>
                </div>
              </div>

              {/* NIWA Station */}
              <div className="bg-white/50 p-3 rounded border border-green-200">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-xs font-semibold text-green-900 uppercase tracking-wide">NIWA Station</span>
                </div>
                <div className="text-xs text-green-700 space-y-0.5">
                  <div>Lat: {preview.actualLatitude.toFixed(6)}</div>
                  <div>Lng: {preview.actualLongitude.toFixed(6)}</div>
                </div>
              </div>

              {/* Distance Badge - Spans both columns */}
              <div className="col-span-2 flex items-center justify-center">
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-sm ${
                  preview.distanceKm < 5
                    ? 'bg-green-100 text-green-800 border border-green-300'
                    : preview.distanceKm < 20
                    ? 'bg-yellow-100 text-yellow-800 border border-yellow-300'
                    : 'bg-orange-100 text-orange-800 border border-orange-300'
                }`}>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                  Distance: {preview.distanceKm.toFixed(1)} km
                </div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-600 text-center py-2">
              Select a location on the map to see NIWA station details
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TidePointMap;
